export const hasPositiveOutcome = status =>
  ["PASSED", "APPROVED", "COMPLETED", "QUALIFIED"].includes(status)

export const QUALIFIER_ELIGIBLE_LABEL = "Qualifier Eligible"

/**
 * Exact Coach Tools `blockingReasons` that mean Qualifier Eligible is unmet.
 * Keep in lockstep with pga-backend:
 * - AffiliateApplicationEligibilityRouter::MEMBERSHIP_BLOCKING_REASON
 * - AffiliateApplicationEligibilityRouter::ASSOCIATE_BLOCKING_REASON
 * - ProspectAffiliateApplicationEligibility::QE_MEMBERSHIP_BLOCKING_REASON
 */
export const AFFILIATE_QE_MEMBERSHIP_BLOCKING_REASONS = [
  "You must be a Qualifier Eligible (QE) prospect or a terminated PGM student (dropped) to apply for Affiliate membership.",
  "Associates cannot apply for Affiliate membership. Start over as a Qualifier Eligible (QE) prospect to apply.",
  "You must be a Qualifier Eligible (QE) prospect to apply for Affiliate membership.",
]

const isQualifierEligibleBlockingReason = reason =>
  AFFILIATE_QE_MEMBERSHIP_BLOCKING_REASONS.includes(String(reason))

/**
 * Coach Tools zeros BGC/QL/age when the Affiliate router does not select a
 * path (not QE, not dropped PGM, not terminated Affiliate). Those stubbed
 * `complete: false` flags are not real criterion scores. Infer QE from
 * `eligible` and whether blockingReasons match the QE membership-gate copy.
 * Affiliate PathBox scores Background Check + Qualifier Eligible only.
 */
export const isQualifierEligibleRequirementMet = eligibility => {
  if (!eligibility) return false
  if (eligibility.eligible === true) return true
  const reasons = eligibility.blockingReasons
  if (!Array.isArray(reasons) || reasons.length === 0) return false
  return !reasons.some(isQualifierEligibleBlockingReason)
}

export const affiliateRequirementsPassed = data => {
  const { prospect } = data || {}
  return {
    "Background Check": hasPositiveOutcome(prospect?.backgroundCheck?.status),
    "Qualifying Level": hasPositiveOutcome(prospect?.qualifyingLevel?.status),
  }
}

export const affiliatePathRequirementsPassed = (data, eligibility) => {
  const { prospect } = data || {}
  return {
    "Background Check": hasPositiveOutcome(prospect?.backgroundCheck?.status),
    [QUALIFIER_ELIGIBLE_LABEL]: isQualifierEligibleRequirementMet(eligibility),
  }
}

export const associateRequirementsPassed = data => {
  const { prospect } = data || {}
  const pat = prospect?.playingAbilityTestStatus
  return {
    ...affiliateRequirementsPassed(data),
    "Player Ability Test (PAT)":
      !pat?.expired && hasPositiveOutcome(pat?.status),
  }
}

const calculateRequirements = requirements => {
  const passedRequirements = Object.values(requirements).filter(
    requirement => requirement,
  ).length
  const totalRequirements = Object.values(requirements).length
  return `${passedRequirements} of ${totalRequirements}`
}

export default calculateRequirements

export const isAssociateRequirementsMet = data =>
  Object.values(associateRequirementsPassed(data)).every(
    requirement => requirement,
  )
export const isAffiliateRequirementsMet = (data, eligibility) =>
  Object.values(affiliatePathRequirementsPassed(data, eligibility)).every(
    requirement => requirement,
  )
